/**
 * Preparing a picked photo for upload.
 *
 * A phone photo is around 4000px and several megabytes. Sending that as a
 * data URL means about a third more again on the wire, for a picture nothing
 * will ever display at full size. So the resizing happens here, before the
 * upload: one copy capped at PHOTO_MAX_PX for viewing, one small one for the
 * grid. Raise PHOTO_MAX_PX if the archive ever needs to be print-grade.
 */

/** Long edge of the stored copy. A4 at 300dpi is 3508px; this is close. */
export const PHOTO_MAX_PX = 2560;

/** Long edge of the grid thumbnail, at twice the size it is shown at. */
const THUMB_MAX_PX = 480;

const PHOTO_QUALITY = 0.82;
const THUMB_QUALITY = 0.7;

/** Refuse a file this big before decoding it; phones do not produce these. */
const MAX_FILE_BYTES = 40 * 1024 * 1024;

/**
 * The most pixels any one canvas here will ask for. iOS stops around 16.7
 * million and returns a blank canvas rather than an error, so staying under
 * it is the difference between a photo and a black rectangle.
 */
const SAFE_PIXELS = 14e6;

export type Prepared = {
  /** data:image/jpeg;base64,… capped at PHOTO_MAX_PX */
  image: string;
  thumb: string;
  width: number;
  height: number;
};

/**
 * Decode a file to something canvas can draw.
 *
 * `createImageBitmap` is the fast path and the only one that can be told to
 * honour the EXIF rotation a phone writes rather than re-encoding the picture
 * sideways. Older Safari has the function but not the option, and can reject
 * the call outright, so an <img> is kept as the fallback — it applies the
 * same rotation itself in current browsers.
 */
async function decode(file: File): Promise<{ src: CanvasImageSource; w: number; h: number }> {
  try {
    const bmp = await createImageBitmap(file, { imageOrientation: 'from-image' });
    return { src: bmp, w: bmp.width, h: bmp.height };
  } catch {
    /* fall through */
  }
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('decode_failed'));
      el.src = url;
    });
    return { src: img, w: img.naturalWidth, h: img.naturalHeight };
  } finally {
    URL.revokeObjectURL(url);
  }
}

function canvasOf(w: number, h: number): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  return c;
}

/**
 * Draw `src` down to fit `max` on its long edge.
 *
 * Two things are going on. Browsers skip filtering when an image is shrunk a
 * long way in one step, which is what turns a 4000px photo into a crunchy
 * thumbnail — so this halves repeatedly until the last step is under 2×.
 *
 * And it never allocates a canvas the size of the source. iOS caps how much
 * canvas it will hand out (around 16 megapixels), and a modern phone's 48
 * megapixel photo is well past it; a canvas over the cap does not throw, it
 * just comes back blank, so the upload would quietly be a black rectangle.
 * Drawing straight from the decoded image into the first reduced step keeps
 * every canvas here comfortably under it.
 */
function fit(src: CanvasImageSource, w: number, h: number, max: number): HTMLCanvasElement {
  const scale = Math.min(1, max / Math.max(w, h));
  const cw = Math.max(1, Math.round(w * scale));
  const ch = Math.max(1, Math.round(h * scale));

  let cur: CanvasImageSource = src;
  let pw = w;
  let ph = h;
  while (pw > cw * 2 && ph > ch * 2) {
    let nw = Math.max(cw, Math.round(pw / 2));
    let nh = Math.max(ch, Math.round(ph / 2));
    // Halving a 108 megapixel photo still lands at 27, which is over the cap.
    // Keep halving the *step* until it is one this device will allocate; the
    // target is at most max×max, so this always terminates.
    while (nw * nh > SAFE_PIXELS && nw > cw * 2 && nh > ch * 2) {
      nw = Math.max(cw, Math.round(nw / 2));
      nh = Math.max(ch, Math.round(nh / 2));
    }
    const next = canvasOf(nw, nh);
    const nctx = next.getContext('2d')!;
    nctx.imageSmoothingQuality = 'high';
    nctx.drawImage(cur, 0, 0, nw, nh);
    cur = next;
    pw = nw;
    ph = nh;
  }

  const out = canvasOf(cw, ch);
  const octx = out.getContext('2d')!;
  octx.imageSmoothingQuality = 'high';
  octx.drawImage(cur, 0, 0, cw, ch);
  return out;
}

/**
 * Turn a picked file into the two copies the server stores, or null if it is
 * not a picture this can handle.
 */
export async function prepare(file: File): Promise<Prepared | null> {
  if (!file.type.startsWith('image/') || file.size > MAX_FILE_BYTES) return null;
  let decoded: { src: CanvasImageSource; w: number; h: number };
  try {
    decoded = await decode(file);
  } catch {
    return null;
  }
  const { src, w, h } = decoded;
  if (!w || !h) return null;

  const big = fit(src, w, h, PHOTO_MAX_PX);
  // The thumbnail comes off the already-reduced copy rather than the original:
  // same result, half the work, and the big canvas is the only one that ever
  // has to hold the full picture.
  const small = fit(big, big.width, big.height, THUMB_MAX_PX);
  if (typeof (src as ImageBitmap).close === 'function') (src as ImageBitmap).close();

  return {
    image: big.toDataURL('image/jpeg', PHOTO_QUALITY),
    thumb: small.toDataURL('image/jpeg', THUMB_QUALITY),
    width: big.width,
    height: big.height,
  };
}

/** "2,4 MB" — sizes are read, not calculated from, so one decimal is plenty. */
export function fileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
}
